import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { useState } from "react";
import useDeliveryMethods from "../../services/useDeliveryMethods";
import useCreateDeliveryMethod from "../../services/useCreateDeliveryMethod";
import useUpdateDeliveryMethod from "../../services/useUpdateDeliveryMethod";
import useDeleteDeliveryMethod from "../../services/useDeleteDeliveryMethod";
import { IDeliveryMethod } from "../../types/seederOrders";

interface IFormData {
  title: string;
  description: string;
  fee: string;
  is_home_delivery: boolean;
}

const DeliveryMethodsManagement = () => {
  const { data: deliveryMethods, isLoading } = useDeliveryMethods();
  const createMutation = useCreateDeliveryMethod();
  const updateMutation = useUpdateDeliveryMethod();
  const deleteMutation = useDeleteDeliveryMethod();

  const [openDialog, setOpenDialog] = useState(false);
  const [editingMethod, setEditingMethod] = useState<IDeliveryMethod | null>(
    null,
  );
  const [formData, setFormData] = useState<IFormData>({
    title: "",
    description: "",
    fee: "",
    is_home_delivery: true,
  });
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const handleOpenDialog = (method?: IDeliveryMethod) => {
    if (method) {
      setEditingMethod(method);
      setFormData({
        title: method.title,
        description: method.description,
        fee: method.fee,
        is_home_delivery: method.is_home_delivery,
      });
    } else {
      setEditingMethod(null);
      setFormData({
        title: "",
        description: "",
        fee: "",
        is_home_delivery: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMethod(null);
    setFormData({
      title: "",
      description: "",
      fee: "",
      is_home_delivery: true,
    });
  };

  const handleSave = () => {
    if (!formData.title.trim()) {
      setMessage({ type: "error", text: "O título é obrigatório." });
      return;
    }

    if (!formData.fee || parseFloat(formData.fee) < 0) {
      setMessage({
        type: "error",
        text: "A taxa deve ser um valor válido e não negativo.",
      });
      return;
    }

    if (editingMethod) {
      updateMutation.mutate(
        {
          id: editingMethod.id,
          ...formData,
        },
        {
          onSuccess: () => {
            setMessage({
              type: "success",
              text: "Método de entrega atualizado com sucesso!",
            });
            handleCloseDialog();
          },
          onError: () => {
            setMessage({
              type: "error",
              text: "Erro ao atualizar método de entrega.",
            });
          },
        },
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          setMessage({
            type: "success",
            text: "Método de entrega criado com sucesso!",
          });
          handleCloseDialog();
        },
        onError: () => {
          setMessage({
            type: "error",
            text: "Erro ao criar método de entrega.",
          });
        },
      });
    }
  };

  const handleDelete = (id: number, title: string) => {
    if (
      window.confirm(
        `Tem certeza que deseja excluir o método de entrega "${title}"?`,
      )
    ) {
      deleteMutation.mutate(id, {
        onSuccess: () => {
          setMessage({
            type: "success",
            text: "Método de entrega excluído com sucesso!",
          });
        },
        onError: () => {
          setMessage({
            type: "error",
            text: "Erro ao excluir método de entrega.",
          });
        },
      });
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography component="h2" variant="h6">
          Métodos de Entrega
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Adicionar
        </Button>
      </Box>

      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : deliveryMethods && deliveryMethods.length > 0 ? (
        <Grid container spacing={2}>
          {deliveryMethods.map((method) => (
            <Grid item xs={12} md={6} key={method.id}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" component="div">
                      {method.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      component="div"
                      color="primary"
                      sx={{ fontWeight: "bold" }}
                    >
                      R$ {parseFloat(method.fee).toFixed(2)}
                    </Typography>
                  </Box>
                  {method.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {method.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {method.is_home_delivery
                      ? "🏠 Entrega em domicílio"
                      : "📍 Retirada"}
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(method)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(method.id, method.title)}
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            p: 4,
            border: "1px dashed #ccc",
            borderRadius: 1,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Nenhum método de entrega cadastrado.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Clique em "Adicionar" para criar o primeiro método.
          </Typography>
        </Box>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMethod
            ? "Editar Método de Entrega"
            : "Novo Método de Entrega"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <TextField
              label="Título"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
              fullWidth
            />
            <TextField
              label="Descrição"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Taxa (R$)"
              type="number"
              value={formData.fee}
              onChange={(e) =>
                setFormData({ ...formData, fee: e.target.value })
              }
              required
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_home_delivery}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      is_home_delivery: e.target.checked,
                    })
                  }
                />
              }
              label="Entrega em domicílio"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            {createMutation.isLoading || updateMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              "Salvar"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryMethodsManagement;
